import { z } from "zod";
import { optionalUuid } from "../lib/zod-helpers";
import { paginationSchema, sortSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro e-mailové šablony v2 (plán §S.2.9).
 *
 * Samostatný CRUD katalog textů. Jedna šablona = jedna znovupoužitelná zpráva
 * s {{mustache}} proměnnými v subjektu i těle. `render` procedura dělá
 * jednoduchý string replace, žádný MJML / Handlebars engine.
 */

const uuidMsg = "Neplatné UUID";

export const emailTemplateJazykEnum = z.enum(["cs", "en", "sk"]);
export type EmailTemplateJazyk = z.infer<typeof emailTemplateJazykEnum>;

export const emailTemplateKategorieEnum = z.enum(["welcome", "reminder", "follow_up", "custom"]);
export type EmailTemplateKategorie = z.infer<typeof emailTemplateKategorieEnum>;

const nazevField = z
  .string()
  .min(1, "Název je povinný")
  .max(100, "Název musí být 100 znaků nebo méně");

const predmetField = z
  .string()
  .min(1, "Předmět je povinný")
  .max(255, "Předmět musí být 255 znaků nebo méně");

const teloHtmlField = z
  .string()
  .min(1, "Tělo šablony je povinné")
  .max(50_000, "HTML tělo musí být 50 000 znaků nebo méně");

const teloTextField = z.string().max(50_000, "Textová verze musí být 50 000 znaků nebo méně");

export const createEmailTemplateSchema = z.object({
  nazev: nazevField,
  predmet: predmetField,
  teloHtml: teloHtmlField,
  teloText: teloTextField.optional(),
  jazyk: emailTemplateJazykEnum.default("cs"),
  kategorie: emailTemplateKategorieEnum,
  aktivni: z.boolean().default(true),
  /**
   * Pokud true a HTML neobsahuje `{{unsubscribeUrl}}`, server při save
   * doplní standardní footer. Marketing šablony by měly mít true,
   * transactional false. Default true kvůli compliance.
   */
  autoUnsubscribeFooter: z.boolean().default(true),
});

export const updateEmailTemplateSchema = z.object({
  id: z.string().uuid(uuidMsg),
  nazev: nazevField.optional(),
  predmet: predmetField.optional(),
  teloHtml: teloHtmlField.optional(),
  teloText: teloTextField.nullable().optional(),
  jazyk: emailTemplateJazykEnum.optional(),
  kategorie: emailTemplateKategorieEnum.optional(),
  aktivni: z.boolean().optional(),
  autoUnsubscribeFooter: z.boolean().optional(),
});

/** Whitelist řazení šablon — router mapuje klíče na sloupce. */
export const EMAIL_TEMPLATES_SORT_KEYS = ["nazev", "kategorie", "aktualizovano"] as const;
export type EmailTemplatesSortKey = (typeof EMAIL_TEMPLATES_SORT_KEYS)[number];

export const listEmailTemplatesSchema = paginationSchema
  .merge(sortSchema(EMAIL_TEMPLATES_SORT_KEYS))
  .extend({
    search: z.string().max(255, "Hledaný text musí být 255 znaků nebo méně").optional(),
    kategorie: emailTemplateKategorieEnum.optional(),
    jazyk: emailTemplateJazykEnum.optional(),
    aktivniOnly: z.coerce.boolean().optional(),
  });

export const emailTemplateIdSchema = z.object({
  id: z.string().uuid(uuidMsg),
});

/** Render preview — vloží proměnné do subjectu i HTML/text verze. */
export const renderEmailTemplateSchema = z.object({
  id: z.string().uuid(uuidMsg),
  payload: z
    .record(z.string().max(100, "Klíč proměnné musí být 100 znaků nebo méně"), z.string().max(5000))
    .default({}),
});

/** Admin odešle test šablony na libovolný e-mail — enqueue do BullMQ. */
export const sendTestEmailSchema = z.object({
  id: z.string().uuid(uuidMsg),
  to: z.preprocess(
    (val) => (typeof val === "string" ? val.trim().toLowerCase() : val),
    z.string().email("Neplatný e-mail").max(255, "E-mail musí být 255 znaků nebo méně"),
  ),
  payload: z
    .record(z.string().max(100, "Klíč proměnné musí být 100 znaků nebo méně"), z.string().max(5000))
    .default({}),
  klientId: optionalUuid(uuidMsg),
});

export type CreateEmailTemplateInput = z.infer<typeof createEmailTemplateSchema>;
export type UpdateEmailTemplateInput = z.infer<typeof updateEmailTemplateSchema>;
export type ListEmailTemplatesInput = z.infer<typeof listEmailTemplatesSchema>;
export type EmailTemplateIdInput = z.infer<typeof emailTemplateIdSchema>;
export type RenderEmailTemplateInput = z.infer<typeof renderEmailTemplateSchema>;
export type SendTestEmailInput = z.infer<typeof sendTestEmailSchema>;
